import { FormattedMessage, useIntl } from 'react-intl';
import React from 'react';
import classNames from 'classnames';
import Card from '../../../components/Card';
import { isProd, isStage } from '../../../constants/config';
import { ReactComponent as UserIcon } from '../../../assets/icons/e_i_user.svg';
import { ReactComponent as DocumentIcon } from '../../../assets/icons/e_i_document.svg';
import { ReactComponent as ExportIcon } from '../../../assets/icons/export.svg';
import { ReactComponent as ImportIcon } from '../../../assets/icons/import.svg';
import { ReactComponent as CategoryIcon } from '../../../assets/icons/category.svg';
import { ReactComponent as PeriodIcon } from '../../../assets/icons/e_i_period.svg';
import { ReactComponent as MailIcon } from '../../../assets/icons/e_i_mail.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/arrow_right.svg';

interface Props {
  userInfo: any;
  isAgency: boolean;
}

export const DashboardCards = ({ userInfo, isAgency }: Props) => {
  const intl = useIntl();

  const getLink = () => {
    const linksProd = {
      cardOne: intl.formatMessage({
        id: 'ec.dashboardService.boxSettingsProdLink',
        defaultMessage: 'https://my.apgsga.ch/ui/profile-form/address',
      }),
      cardTwo: intl.formatMessage({
        id: 'ec.dashboardService.boxNoticeProdLink',
        defaultMessage: 'https://my.apgsga.ch/ui/news-form',
      }),
      cardThree: intl.formatMessage({
        id: 'ec.dashboardService.boxDocumentsProdLink',
        defaultMessage: 'https://my.apgsga.ch/ui/auftragsdokumente-home-panel',
      }),
      cardFour: intl.formatMessage({
        id: 'ec.dashboardService.boxReportingProdLink',
        defaultMessage: 'https://my.apgsga.ch/ui/reporting-home-panel',
      }),
      cardFive: intl.formatMessage({
        id: 'ec.dashboardService.boxBookingProdLink',
        defaultMessage: 'https://apgsga.ch/de/aussenwerbung/plakate-online-buchen/',
      }),
      cardSix: intl.formatMessage({
        id: 'ec.dashboardService.boxToMyHubProdLink',
        defaultMessage: 'https://my.apgsga.ch/',
      }),
      cardSeven: intl.formatMessage({
        id: 'ec.dashboardService.agencyLinkExport',
        defaultMessage: 'https://agency.ecom.apgsga.ch/export',
      }),
      cardEight: intl.formatMessage({
        id: 'ec.dashboardService.agencyLinkImport',
        defaultMessage: 'https://agency.ecom.apgsga.ch/import',
      }),
    };

    const linksStage = {
      cardOne: intl.formatMessage({
        id: 'ec.dashboardService.boxSettingsStageLink',
        defaultMessage: 'https://usersettings-t1.apgsga.ch/',
      }),
      cardTwo: intl.formatMessage({
        id: 'ec.dashboardService.boxNoticeStageLink',
        defaultMessage: 'https://my-t1.apgsga.ch/ui/news-homepanel/messages',
      }),
      cardThree: intl.formatMessage({
        id: 'ec.dashboardService.boxDocumentsStageLink',
        defaultMessage: 'https://my-t1.apgsga.ch/ui/auftragsdokumente-homepanel',
      }),
      cardFour: intl.formatMessage({
        id: 'ec.dashboardService.boxReportingStageLink',
        defaultMessage: 'https://my-t1.apgsga.ch/ui/reporting-homepanel',
      }),
      cardFive: intl.formatMessage({
        id: 'ec.dashboardService.boxBookingStageLink',
        defaultMessage:
          'https://apg-website-2021-private-cloud-stage.apg.divio.cloud/de/aussenwerbung/plakate-online-buchen/',
      }),
      cardSix: intl.formatMessage({
        id: 'ec.dashboardService.boxToMyHubStageLink',
        defaultMessage: 'https://my-t1.apgsga.ch/',
      }),
      cardSeven: intl.formatMessage({
        id: 'ec.dashboardService.agencyLinkExportStage',
        defaultMessage: 'https://agency.ecom-stage.apgsga.ch/export',
      }),
      cardEight: intl.formatMessage({
        id: 'ec.dashboardService.agencyLinkImportStage',
        defaultMessage: 'https://agency.ecom-stage.apgsga.ch/import',
      }),
    };

    const linksDev = {
      cardOne: intl.formatMessage({
        id: 'ec.dashboardService.boxSettingsDevLink',
        defaultMessage: 'https://usersettings-t1.apgsga.ch/',
      }),
      cardTwo: intl.formatMessage({
        id: 'ec.dashboardService.boxNoticeDevLink',
        defaultMessage: 'https://my-t1.apgsga.ch/ui/news-homepanel/messages',
      }),
      cardThree: intl.formatMessage({
        id: 'ec.dashboardService.boxDocumentsDevLink',
        defaultMessage: 'https://my-t1.apgsga.ch/ui/auftragsdokumente-homepanel',
      }),
      cardFour: intl.formatMessage({
        id: 'ec.dashboardService.boxReportingDevLink',
        defaultMessage: 'https://my-t1.apgsga.ch/ui/reporting-homepanel',
      }),
      cardFive: intl.formatMessage({
        id: 'ec.dashboardService.boxBookingDevLink',
        defaultMessage:
          'https://apg-website-2021-private-cloud-stage.apg.divio.cloud/de/aussenwerbung/plakate-online-buchen/',
      }),
      cardSix: intl.formatMessage({
        id: 'ec.dashboardService.boxToMyHubDevLink',
        defaultMessage: 'https://my-t1.apgsga.ch/',
      }),
      cardSeven: intl.formatMessage({
        id: 'ec.dashboardService.agencyLinkExportDev',
        defaultMessage: 'https://agency.ecom-stage.apgsga.ch/export',
      }),
      cardEight: intl.formatMessage({
        id: 'ec.dashboardService.agencyLinkImportDev',
        defaultMessage: 'https://agency.ecom-stage.apgsga.ch/import',
      }),
    };

    return isProd ? linksProd : isStage ? linksStage : linksDev;
  };

  const links = getLink();

  return (
    <div className='service-dashboard'>
      <div className={classNames('row justify-content-start')}>
        <div className='d-flex flex-column'>
          <h2 className='mb-3 ps-0 fw-bold page-title'>
            <FormattedMessage id='as.sujet.benutzerkonto' defaultMessage='Dashboard' />
          </h2>

          <p className='mt-2 mb-4'>
            <FormattedMessage id='as.sujet.helloUser' defaultMessage='Hallo' /> {userInfo?.firstname}{' '}
            {userInfo?.lastname}
          </p>

          <p className='mb-48'>
            <FormattedMessage
              id='as.sujet.willkomen-text'
              defaultMessage='Willkommen auf Ihrem persönlichen Dashboard. Hier finden Sie den Zugang zu Ihrem Benutzerkonto, Ihren gebuchten Kampagnen, können Rechnungen einsehen, Plakat- und Gestaltungsaufträge für Ihre Kampagnen erstellen und vieles mehr.'
            />
          </p>
        </div>

        <div className='d-flex flex-wrap cards'>
          <div className='card-item'>
            <Card
              to={links['cardOne']}
              icon={<UserIcon />}
              title={intl.formatMessage({
                id: 'ec.dashboardService.cardOne',
                defaultMessage: 'Einstellungen',
              })}
              cardRow={true}
              openLinkInNewTab
            />
          </div>

          <div className='card-item'>
            <Card
              to={links['cardTwo']}
              icon={<MailIcon />}
              title={intl.formatMessage({
                id: 'ec.dashboardService.cardTwo',
                defaultMessage: 'Mitteilungen',
              })}
              cardRow={true}
              openLinkInNewTab
            />
          </div>

          <div className='card-item'>
            <Card
              to={links['cardThree']}
              icon={<DocumentIcon />}
              title={intl.formatMessage({
                id: 'ec.dashboardService.cardThree',
                defaultMessage: 'Auftragsdokumente',
              })}
              cardRow={true}
              openLinkInNewTab
            />
          </div>

          <div className='card-item'>
            <Card
              to={links['cardFour']}
              icon={<CategoryIcon />}
              title={intl.formatMessage({
                id: 'ec.dashboardService.cardFour',
                defaultMessage: 'Reporting',
              })}
              cardRow={true}
              openLinkInNewTab
            />
          </div>

          <div className='card-item'>
            <Card
              to={links['cardFive']}
              icon={<PeriodIcon />}
              title={intl.formatMessage({
                id: 'ec.dashboardService.cardFive',
                defaultMessage: 'Planen & buchen',
              })}
              cardRow={true}
            />
          </div>

          <div className='card-item'>
            <Card
              to={links['cardSix']}
              icon={<ArrowIcon />}
              title={intl.formatMessage({
                id: 'ec.dashboardService.cardSix',
                defaultMessage: 'zu myAPG',
              })}
              cardRow={true}
              openLinkInNewTab
            />
          </div>

          {isAgency && (
            <div className='card-item'>
              <Card
                to={links['cardSeven']}
                icon={<ExportIcon />}
                title={intl.formatMessage({
                  id: 'ec.dashboardService.cardSeven',
                  defaultMessage: 'Flächenliste exportieren',
                })}
                cardRow={true}
                openLinkInNewTab
              />
            </div>
          )}

          {isAgency && (
            <div className='card-item'>
              <Card
                to={links['cardEight']}
                icon={<ImportIcon />}
                title={intl.formatMessage({
                  id: 'ec.dashboardService.cardEight',
                  defaultMessage: 'Flächenliste importieren',
                })}
                cardRow={true}
                openLinkInNewTab
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
